import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { MentionsInput, Mention } from 'react-mentions'
import deburr from 'lodash.deburr'
import { popoverStyles } from 'components/Popover/Content/PopoverContent'
import { dropdownItemStyles } from 'components/Dropdown'
import emojiAliases from './emojiAliases'
import memoize from 'lodash.memoize'
import merge from 'lodash.merge'
import { Truncate } from 'components/Truncate'
import Box, { Flex } from 'components/Box'
import Breakpoint from 'components/Breakpoint'
import Avatar from 'components/Avatar'

const shouldSubmit = e => e.keyCode === 13 && (e.metaKey || e.ctrlKey)

export function renderEmojiSuggestion(entry, search, highlightedDisplay) {
  return (
    <Box title={entry.id}>
      <span>
        {highlightedDisplay} {entry.id}
      </span>
    </Box>
  )
}

const AvatarContainer = styled.div`
  margin-right: 4px;
  flex-shrink: 0;
`

const strippedString = string => deburr(string.toLowerCase())

const customHighlightDisplay = (display, query) => {
  const strippedDisplay = strippedString(display)
  const strippedQuery = strippedString(query)
  const i = strippedDisplay.indexOf(strippedQuery)

  if (i === -1) {
    return display
  }

  return (
    <>
      {display.substring(0, i)}
      <b>{display.substring(i, i + query.length)}</b>
      {display.substring(i + query.length)}
    </>
  )
}

export const renderUserSuggestion = (entry, search) => (
  <Flex title={entry.display} alignItems="center">
    <AvatarContainer>
      <Avatar name={entry.display} src={entry.avatar} size="1.5rem" />
    </AvatarContainer>
    <Truncate>{customHighlightDisplay(entry.display, search)}</Truncate>
  </Flex>
)

const emoji = Object.entries(emojiAliases)
  .sort(([a], [b]) => a.length - b.length)
  .map(a => ({ id: a[0], display: a[1] }))

const prefixComparatorFor = query => ({ id: firstId }, { id: secondId }) => {
  const firstStartsWith = firstId.startsWith(query)
  const secondStartsWith = secondId.startsWith(query)

  if (firstStartsWith && secondStartsWith) {
    return 0
  } else if (firstStartsWith) {
    return -1
  }

  return 1
}

const idIncludes = query => ({ id }) => {
  return id.includes(query)
}

const getEmojisListBySearch = query =>
  emoji.filter(idIncludes(query)).sort(prefixComparatorFor(query)).slice(0, 5)

const namePrefixComparatorFor = query => (
  { display: firstDisplay },
  { display: secondDisplay }
) => {
  const firstStartsWith = firstDisplay.toLowerCase().startsWith(query)
  const secondStartsWith = secondDisplay.toLowerCase().startsWith(query)

  if (firstStartsWith && secondStartsWith) {
    return 0
  } else if (firstStartsWith) {
    return -1
  }

  return 1
}

const nameIncludes = query => ({ display }) => {
  const strippedQuery = strippedString(query)
  const strippedName = strippedString(display)
  return strippedName.includes(strippedQuery)
}

const getUsersListBySearch = (users, query) =>
  users
    .filter(nameIncludes(query))
    .sort(namePrefixComparatorFor(query))
    .slice(0, 5)

const defaultEmoji = [
  { id: '+1', display: '👍' },
  { id: '-1', display: '👎' },
  { id: 'white_check_mark', display: '✅' },
  { id: '100', display: '💯' },
]

const mentionsInputStyles = {
  highlighter: {
    padding: '1.6rem 1.75rem .5rem',
    lineHeight: '1.25rem',

    strong: {
      fontWeight: 'bold',
    },
  },

  input: {
    padding: '1rem 1.75rem',
    lineHeight: '1.25rem',
    outline: 0,
    border: 0,
    maxHeight: '230px',
    overflowY: 'auto',
  },
}

const StyledMentionsInput = styled(MentionsInput)`
  textarea {
    ::placeholder {
      font-style: italic;
    }
  }
`

const SuggestionsInput = ({
  padding,
  mentionableUsers,
  style,
  onKeyDown,
  onSubmit,
  ...props
}) => {
  const handleKeyDown = useCallback(
    e => {
      if (onKeyDown) onKeyDown(e)
      if (shouldSubmit(e) && onSubmit) onSubmit(e)
    },
    [onKeyDown, onSubmit]
  )

  const searchEmoji = memoize(search => {
    if (search.length === 0) return defaultEmoji

    return getEmojisListBySearch(search.toLowerCase())
  })

  const searchUser = memoize(search => {
    return getUsersListBySearch(mentionableUsers, search.toLowerCase())
  })

  const additionalMentionsInputStyle = merge(mentionsInputStyles, {
    highlighter: { padding },
    input: { padding },
    ...style,
  })

  return (
    <Breakpoint on="sm">
      {matches => {
        /*
        We shouldn't show the emoji trigger in mobile, since most phone keyboards
        have emoji support build in.

        https://github.com/sketch-hq/Cloud/issues/2004#issuecomment-539848866
        */

        return (
          <StyledMentionsInput
            placeholder="Write a comment..."
            allowSpaceInQuery
            style={additionalMentionsInputStyle}
            {...props}
            onKeyDown={handleKeyDown}>
            <Mention
              type="user"
              trigger="@"
              markup="@[__display__](user:__id__)"
              data={searchUser}
              displayTransform={(id, display) => `@${display}`}
              renderSuggestion={renderUserSuggestion}
              appendSpaceOnAdd
            />
            {matches && (
              <Mention
                type="emoji"
                trigger=":"
                markup="__display__"
                regex={/($a)/}
                data={searchEmoji}
                renderSuggestion={renderEmojiSuggestion}
                appendSpaceOnAdd
              />
            )}
          </StyledMentionsInput>
        )
      }}
    </Breakpoint>
  )
}

SuggestionsInput.defaultProps = {
  padding: '1.6rem 1.75rem .5rem',
  mentionableUsers: [],
}

SuggestionsInput.propTypes = {
  mentionableUsers: PropTypes.array,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onKeyDown: PropTypes.func,
  onSubmit: PropTypes.func,
  style: PropTypes.object,
}

const StyledSuggestionsInput = styled(SuggestionsInput)`
  ${({ theme: { fonts, colors, fontSizes } }) => css`
    font-size: ${fontSizes.small};
    font-family: ${fonts.primary};

    ul {
      ${popoverStyles};
      overflow: hidden;
    }

    li {
      ${dropdownItemStyles};
      padding: 12px 16px 12px 20px;
      &::first-letter {
        /* This non-theme font-size precisely sizes the emoji in the picker */
        font-size: 22px; /* stylelint-disable-line scales/font-size */
        margin-right: 2px; /* stylelint-disable-line scales/space */
        display: inline-block;
        vertical-align: middle;
      }
    }

    & [class*='--focused'] {
      background: ${colors.background.tertiary[1]};
      color: ${colors.foreground.secondary[2]};
    }

    & div[class*='__suggestions'] {
      width: calc(100% - 2rem);
      margin: auto;
      bottom: 100%;
    }
  `}
`

export default StyledSuggestionsInput
